import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Icon from 'common/components/Elements/Icon/Icon';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal, { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import Commands from 'core/components/Elements/Commands/Commands';
import type { EnhancedCommand } from 'core/utilities/core.types';
import React, { useState } from 'react';
import type { Sequence, SequenceFeatureColor, SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import Features from 'sequenceAnalyser/components/Features/Features/Features';
import type { HandleFeatureDefinitionsChange } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import type { FeatureDefinitions, FeatureTypesColor } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';

export interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  onFeatureDefinitionsChange: HandleFeatureDefinitionsChange;
  userColors: SequenceFeatureColor[];
  featureDefinitions: FeatureDefinitions;
  featureTypesColor: FeatureTypesColor;
  hideTable?: boolean;
  sequences: Sequence[];
  sequenceType: SequenceType;
}

function FeaturesModal({
  open, onOpen, onFeatureDefinitionsChange, userColors, featureDefinitions, hideTable, featureTypesColor,
  sequences, sequenceType,
}: ComponentProps) {
  const [showTable, setShowTable] = useState(false);

  const closeCommand: EnhancedCommand = {
    id: 'close',
    title: 'Close',
    active: true,
    label: 'Close',
    onClick: () => onOpen(false),
    commands: [],
  };

  const tableToggleCommand: EnhancedCommand = {
    id: 'tableToggle',
    title: showTable ? 'Hide Table' : 'Show Table',
    active: true,
    label: showTable ? 'Hide Table' : 'Show Table',
    iconEl: showTable
      ? <Icon align="middle" element={VisibilityOffIcon} mr />
      : <Icon align="middle" element={VisibilityIcon} mr />,
    onClick: () => setShowTable((prevState) => !prevState),
    commands: [],
    action: 'secondary',
    small: true,
  };

  return (
    <SimpleModal
      title="Features"
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      size="full"
      draggablePoppover={!showTable}
      width={showTable ? undefined : 500}
      headerActions={(
        <Commands
          id="FeaturesModalLeftCommands"
          disableResponsive
          commands={hideTable ? [] : [tableToggleCommand]}
        />
      )}
      content={(
        <Box mt={1}>
          <ScrollableModalContent>
            <Features
              userColors={userColors}
              featureDefinitions={featureDefinitions}
              featureTypesColor={featureTypesColor}
              onChange={onFeatureDefinitionsChange}
              tableView={showTable}
              sequences={sequences}
              sequenceType={sequenceType}
            />
          </ScrollableModalContent>

          <ModalActions
            rightActions={(
              <Box mt={-1}>
                <Commands
                  id="FeaturesModalRightCommands"
                  disableResponsive
                  commands={[closeCommand]}
                />
              </Box>
            )}
          />
        </Box>
      )}
    />
  );
}

export default FeaturesModal;
